import type { Dexie, DexieOptions, Table } from 'dexie';
import * as Y from 'yjs';

import { Logger } from '@gitbook/util-debug';

export const HiveDataSyncCacheOrigin = Symbol('HiveDataSyncCacheOrigin');

const DB_NAME = 'hive-docs';

const logger = Logger('hive:browsercache');

export type CreateDexieFn = (tableName: string, options?: DexieOptions) => Dexie;

interface UpdateEntry {
    /**
     * The guid of the document.
     */
    guid: string;
    /**
     * The update value.
     */
    value: Uint8Array;
    /**
     * The timestamp of the update.
     */
    at: number;
    /**
     * If true it means that the update is the entire state of the document.
     */
    state: boolean;
}

/**
 * Limit of updates to keep in the cache for a document.
 * When the limit is reached, we reset to only store one entire update of the entire document.
 */
const MAX_UPDATES_PER_GUID = 100;

/**
 * Cache the data sync Yjs doc in the browser IndexedDB.
 * Inspired by https://github.com/yjs/y-indexeddb
 */
export class HiveDataSyncBrowserCache {
    protected db: Dexie;
    private updateCountByGuid: Map<string, number> = new Map();

    constructor(createDexie: CreateDexieFn, prefix = '') {
        this.db = createDexie(`${prefix}${DB_NAME}`);
        this.db.version(1).stores({
            updates: '++id,guid,at,state',
        });
    }

    /**
     * Get the dexie tables for updates.
     */
    protected get updates(): Table<UpdateEntry> {
        return this.db.table<UpdateEntry>('updates');
    }

    /**
     * Clear the cache.
     */
    public async clear(): Promise<void> {
        await this.db.delete();
    }

    /**
     * Register a document to synchronize it with the cache.
     */
    public registerDoc(doc: Y.Doc) {
        const syncWithDoc = () => {
            const onUpdate = (update: Uint8Array, origin: any) => {
                if (origin !== HiveDataSyncCacheOrigin) {
                    this.pushUpdate(doc, update).catch((error) => {
                        logger.error(`error pushing update for ${doc.guid}`, error);
                    });
                }
            };

            const onDestroy = () => {
                doc.off('updateV2', onUpdate);
                doc.off('destroy', onDestroy);
            };

            doc.on('updateV2', onUpdate);
            doc.on('destroy', onDestroy);

            // Fetch the updates from the cache
            this.fetchUpdates(doc).catch((error) => {
                logger.error(`error fetching updates for ${doc.guid}`, error);
            });
        };

        if (this.db.isOpen()) {
            syncWithDoc();
        } else {
            this.db.on('ready', syncWithDoc);
            this.db.open().catch((error) => {
                logger.error('error opening IndexedDB', error);
            });
        }
    }

    /**
     * Fetch the updates from the cache for a document.
     */
    private async fetchUpdates(doc: Y.Doc) {
        const updates = await this.updates.where('guid').equals(doc.guid).toArray();
        if (!updates.length) {
            logger.info(`no updates found for ${doc.guid}`);
            return;
        }

        logger.info(`fetched ${updates.length} updates for ${doc.guid}`);

        // Apply the updates
        let updateSize = 0;
        Y.transact(
            doc,
            () => {
                for (const update of updates) {
                    doc.transact(() => {
                        updateSize += update.value.byteLength;
                        Y.applyUpdateV2(doc, update.value, HiveDataSyncCacheOrigin);
                    }, HiveDataSyncCacheOrigin);
                }
            },
            HiveDataSyncCacheOrigin,
            false
        );

        // Notify the document that it has been loaded
        logger.info(`loaded ${updates.length} updates (${updateSize} bytes) for ${doc.guid}`);
        doc.emit('load', [doc]);
        this.updateCountByGuid.set(doc.guid, updates.length);
    }

    /**
     * Push an update to the cache.
     */
    private async pushUpdate(doc: Y.Doc, update: Uint8Array): Promise<void> {
        const updateCount = this.updateCountByGuid.get(doc.guid) ?? 0;

        // If we have reached the limit of updates to keep in the cache, we reset the cache
        if (updateCount >= MAX_UPDATES_PER_GUID) {
            await this.resetDoc(doc);
            return;
        }

        logger.info(
            `push update for ${doc.guid} (${update.byteLength} bytes, ${updateCount + 1} updates)`
        );
        const at = Date.now();
        await this.updates.add({
            guid: doc.guid,
            value: update,
            at,
            state: false,
        });

        this.updateCountByGuid.set(doc.guid, (this.updateCountByGuid.get(doc.guid) ?? 0) + 1);
    }

    /**
     * Reset a document in the cache to match the entire state of the document.
     */
    private async resetDoc(doc: Y.Doc) {
        const update = Y.encodeStateAsUpdateV2(doc);
        logger.info(`reset cache for ${doc.guid} with ${update.byteLength} bytes`);

        await this.db.transaction('rw', this.updates, async () => {
            // Delete all updates for the document
            await this.updates.where('guid').equals(doc.guid).delete();

            // Push the current state of the document
            const at = Date.now();
            await this.updates.add({
                guid: doc.guid,
                value: update,
                at,
                state: true,
            });
        });

        this.updateCountByGuid.set(doc.guid, 0);
    }
}
